import React, { useState, useEffect } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import { useJsApiLoader, Autocomplete } from "@react-google-maps/api";
import config from "../config";
import BackButton from "./BackButton";

const GOOGLE_API_KEY = process.env.REACT_APP_GOOGLE_API_KEY;
const MAP_LIBRARIES = ["places"];

const Register = () => {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    email: "",
    password: "",
    confirmPassword: "",
    first_name: "",
    last_name: "",
    phone: "",
    address: "",
    city: "",
    state: "",
    zip_code: "",
    country: "",
  });

  const [termsContent, setTermsContent] = useState(null);
  const [errors, setErrors] = useState({});
  const [termsAccepted, setTermsAccepted] = useState(false);
  const [showTerms, setShowTerms] = useState(false);
  const [notificationMessage, setNotificationMessage] = useState("");
  const [notificationStatus, setNotificationStatus] = useState("");
  const [isNotification, setIsNotification] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [autocomplete, setAutocomplete] = useState(null);
  const [isAddressLoading, setIsAddressLoading] = useState(false);
  const [isFormComplete, setIsFormComplete] = useState(false);

  // Load Google Maps API with a callback to improve performance
  const { isLoaded } = useJsApiLoader({
    googleMapsApiKey: GOOGLE_API_KEY,
    libraries: MAP_LIBRARIES,
    preventGoogleFontsLoading: true,
  });

  const handlePlaceSelect = () => {
    if (autocomplete) {
      setIsAddressLoading(true);
      const place = autocomplete.getPlace();
      if (place) {
        const { formatted_address, address_components } = place;
        const addressDetails = {
          address: formatted_address || "",
          city: "",
          state: "",
          country: "",
          zip_code: "",
        };

        address_components.forEach((component) => {
          const types = component.types;
          if (types.includes("locality"))
            addressDetails.city = component.long_name;
          if (types.includes("administrative_area_level_1"))
            addressDetails.state = component.long_name;
          if (types.includes("country"))
            addressDetails.country = component.long_name;
          if (types.includes("postal_code"))
            addressDetails.zip_code = component.long_name;
        });

        setFormData((prevData) => ({
          ...prevData,
          ...addressDetails,
        }));
      }
      setIsAddressLoading(false);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    if (name === "phone") {
      const phone = value.replace(/\D/g, "").slice(0, 10);
      setFormData({ ...formData, [name]: phone });
    } else {
      setFormData({ ...formData, [name]: value });
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setErrors({});
    if (!termsAccepted) {
      setNotificationMessage("Please accept the Terms and Conditions");
      setNotificationStatus("error");
      setIsNotification(true);
      setIsSubmitting(false);
      setTimeout(() => {
        setIsNotification(false);
      }, 9000);
      return;
    }

    if (formData.password !== formData.confirmPassword) {
      setNotificationMessage("Passwords do not match");
      setNotificationStatus("error");
      setIsNotification(true);
      setIsSubmitting(false);
      setTimeout(() => {
        setIsNotification(false);
      }, 3000);
      return;
    }

    const { confirmPassword, ...dataToSend } = formData;

    try {
      const response = await axios.post(
        `${config.API_BASE_URL}/auth/register`,
        dataToSend
      );
      setNotificationMessage(response.data.message);
      setNotificationStatus("success");
      setIsNotification(true);
      setTimeout(() => {
        setIsNotification(false);
        navigate("/login");
      }, 5000);
    } catch (error) {
      setIsSubmitting(false);
      console.log("error", error);
      if (
        error.response &&
        error.response.data &&
        error.response.data.message
      ) {
        setNotificationMessage(error.response.data.message);
        setNotificationStatus("error");
      } else {
        setNotificationMessage("An error occurred during registration");
        setNotificationStatus("error");
      }
      setIsNotification(true);
      setTimeout(() => {
        setIsNotification(false);
      }, 5000);
    } finally {
      setIsSubmitting(false);
    }
  };

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  useEffect(() => {
    const requiredFields = [
      "email",
      "password",
      "confirmPassword",
      "first_name",
      "last_name",
      "phone",
      "address",
      "city",
      "state",
      "zip_code",
      "country",
    ];

    const allFieldsFilled = requiredFields.every(
      (field) => formData[field] && formData[field].trim() !== ""
    );

    setIsFormComplete(allFieldsFilled && termsAccepted);
  }, [formData, termsAccepted]);

  // Terms and Conditions content
  const handleShowTerms = () => {
    if (!termsContent) {
      setTermsContent(
        <div className="p-6 max-h-96 overflow-y-auto">
          <h3 className="text-xl font-bold mb-4">Terms and Conditions</h3>

          <h4 className="font-semibold mt-4 mb-2">1. Acceptance of Terms</h4>
          <p className="text-sm mb-2">
            By registering for an account with our restaurant table service
            application, you agree to be bound by these Terms and Conditions. If
            you do not agree to these terms, please do not use our service.
          </p>

          <h4 className="font-semibold mt-4 mb-2">2. User Registration</h4>
          <p className="text-sm mb-2">
            To use our services, you must register for an account. You agree to
            provide accurate, current, and complete information during the
            registration process and to update such information to keep it
            accurate, current, and complete.
          </p>

          <h4 className="font-semibold mt-4 mb-2">3. Privacy Policy</h4>
          <p className="text-sm mb-2">
            Your privacy is important to us. Our Privacy Policy explains how we
            collect, use, and protect your personal information. By using our
            service, you consent to our collection and use of personal data as
            outlined in our Privacy Policy.
          </p>

          <h4 className="font-semibold mt-4 mb-2">4. User Conduct</h4>
          <p className="text-sm mb-2">
            You agree not to use the service for any illegal or unauthorized
            purpose. You must not attempt to gain unauthorized access to any
            portion of the service or any other systems or networks connected to
            the service.
          </p>

          <h4 className="font-semibold mt-4 mb-2">5. Service Availability</h4>
          <p className="text-sm mb-2">
            We strive to provide uninterrupted service, but we do not guarantee
            that the service will be available at all times. We reserve the
            right to suspend or terminate the service at any time without
            notice.
          </p>

          <h4 className="font-semibold mt-4 mb-2">6. Modifications to Terms</h4>
          <p className="text-sm mb-2">
            We reserve the right to modify these Terms at any time. Your
            continued use of the service after any such changes constitutes your
            acceptance of the new Terms.
          </p>

          <h4 className="font-semibold mt-4 mb-2">
            7. Limitation of Liability
          </h4>
          <p className="text-sm mb-2">
            To the maximum extent permitted by law, we shall not be liable for
            any indirect, incidental, special, consequential, or punitive
            damages resulting from your use or inability to use the service.
          </p>

          <h4 className="font-semibold mt-4 mb-2">8. Governing Law</h4>
          <p className="text-sm mb-2">
            These Terms shall be governed by and construed in accordance with
            the laws of the jurisdiction in which our company is registered,
            without regard to its conflict of law provisions.
          </p>

          <h4 className="font-semibold mt-4 mb-2">9. Contact Information</h4>
          <p className="text-sm mb-2">
            If you have any questions about these Terms, please contact us at
            support@rtsa.com.
          </p>
        </div>
      );
    }
    setShowTerms(true);
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 py-8 px-4 sm:px-6 lg:px-8">
      <div className="max-w-2xl mx-auto">
        <BackButton />
        
        {/* Header Section */}
        <div className="text-center mb-8">
          <div className="bg-white rounded-full p-3 shadow-lg inline-flex items-center justify-center mb-4">
            <div className="w-12 h-12 bg-gradient-to-r from-blue-500 to-purple-600 rounded-full flex items-center justify-center">
              <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
              </svg>
            </div>
          </div>
          <h1 className="text-3xl font-bold text-gray-900 mb-2">
            Create Your Account
          </h1>
          <p className="text-gray-600">
            Join us and start your culinary journey
          </p>
        </div>

        {/* Notification */}
        {isNotification && (
          <div className={`mb-6 p-4 rounded-lg border-l-4 ${
            notificationStatus === "error" 
              ? "bg-red-50 border-red-400 text-red-700" 
              : "bg-green-50 border-green-400 text-green-700"
          } shadow-sm`}>
            <div className="flex items-center">
              <svg className={`w-5 h-5 mr-3 ${
                notificationStatus === "error" ? "text-red-400" : "text-green-400"
              }`} fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
              </svg>
              <p className="text-sm font-medium">{notificationMessage}</p>
            </div>
          </div>
        )}

        {/* Main Form Card */}
        <div className="bg-white rounded-2xl shadow-xl overflow-hidden">
          <div className="p-8">
            <form onSubmit={handleSubmit} className="space-y-6">
              {/* Account Information Section */}
              <div className="space-y-4">
                <div className="flex items-center space-x-3 mb-4">
                  <div className="w-2 h-6 bg-blue-500 rounded-full"></div>
                  <h3 className="text-lg font-semibold text-gray-900">Account Information</h3>
                </div>
                
                <div className="grid grid-cols-1 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Email Address
                    </label>
                    <input
                      type="email"
                      name="email"
                      value={formData.email}
                      onChange={handleChange}
                      required
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200"
                      placeholder="Enter your email"
                    />
                  </div>

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Password
                      </label>
                      <input
                        type="password"
                        name="password"
                        value={formData.password}
                        onChange={handleChange}
                        required
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200"
                        placeholder="Create password"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Confirm Password
                      </label>
                      <input
                        type="password"
                        name="confirmPassword"
                        value={formData.confirmPassword}
                        onChange={handleChange}
                        required
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200"
                        placeholder="Confirm password"
                      />
                    </div>
                  </div>
                </div>
              </div>

              {/* Personal Information Section */}
              <div className="space-y-4 pt-6 border-t border-gray-200">
                <div className="flex items-center space-x-3 mb-4">
                  <div className="w-2 h-6 bg-green-500 rounded-full"></div>
                  <h3 className="text-lg font-semibold text-gray-900">Personal Information</h3>
                </div>
                
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      First Name
                    </label>
                    <input
                      type="text"
                      name="first_name"
                      value={formData.first_name}
                      onChange={handleChange}
                      required
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200"
                      placeholder="First name"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Last Name
                    </label>
                    <input
                      type="text"
                      name="last_name"
                      value={formData.last_name}
                      onChange={handleChange}
                      required
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200"
                      placeholder="Last name"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Phone Number
                  </label>
                  <input
                    type="tel"
                    name="phone"
                    value={formData.phone}
                    onChange={handleChange}
                    pattern="[0-9]{10}"
                    maxLength="10"
                    required
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200"
                    placeholder="(555) 123-4567"
                  />
                </div>
              </div>

              {/* Address Information Section */}
              <div className="space-y-4 pt-6 border-t border-gray-200">
                <div className="flex items-center space-x-3 mb-4">
                  <div className="w-2 h-6 bg-purple-500 rounded-full"></div>
                  <h3 className="text-lg font-semibold text-gray-900">Address Information</h3>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Address
                  </label>
                  <div className="relative">
                    {isLoaded ? (
                      <Autocomplete
                        onLoad={setAutocomplete}
                        onPlaceChanged={handlePlaceSelect}
                      >
                        <input
                          type="text"
                          placeholder="Search your address"
                          className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200"
                        />
                      </Autocomplete>
                    ) : (
                      <input
                        type="text"
                        name="address"
                        value={formData.address}
                        onChange={handleChange}
                        required
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200"
                        placeholder="Enter your address"
                      />
                    )}
                    {isAddressLoading && (
                      <div className="absolute right-3 top-1/2 transform -translate-y-1/2">
                        <svg
                          className="animate-spin h-5 w-5 text-blue-500"
                          xmlns="http://www.w3.org/2000/svg"
                          fill="none"
                          viewBox="0 0 24 24"
                        >
                          <circle
                            className="opacity-25"
                            cx="12"
                            cy="12"
                            r="10"
                            stroke="currentColor"
                            strokeWidth="4"
                          ></circle>
                          <path
                            className="opacity-75"
                            fill="currentColor"
                            d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                          ></path>
                        </svg>
                      </div>
                    )}
                  </div>
                  {formData.address && (
                    <p className="mt-2 text-xs text-gray-500 bg-blue-50 px-3 py-1 rounded">
                      Selected: {formData.address}
                    </p>
                  )}
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      City
                    </label>
                    <input
                      type="text"
                      name="city"
                      value={formData.city}
                      onChange={handleChange}
                      required
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200"
                      placeholder="City"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      State
                    </label>
                    <input
                      type="text"
                      name="state"
                      value={formData.state}
                      onChange={handleChange}
                      required
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200"
                      placeholder="State"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Zip Code
                    </label>
                    <input
                      type="text"
                      name="zip_code"
                      value={formData.zip_code}
                      onChange={handleChange}
                      required
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200"
                      placeholder="Zip code"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Country
                  </label>
                  <input
                    type="text"
                    name="country"
                    value={formData.country}
                    onChange={handleChange}
                    required
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200"
                    placeholder="Country"
                  />
                </div>
              </div>

              {/* Terms and Conditions */}
              <div className="pt-6 border-t border-gray-200">
                <div className="flex items-start space-x-3">
                  <input
                    type="checkbox"
                    id="terms"
                    checked={termsAccepted}
                    onChange={(e) => setTermsAccepted(e.target.checked)}
                    required
                    className="mt-1 h-5 w-5 text-blue-600 focus:ring-blue-500 border-gray-300 rounded transition-all duration-200"
                  />
                  <label
                    htmlFor="terms"
                    className="text-sm text-gray-700 leading-5"
                  >
                    I agree to the{" "}
                    <button
                      type="button"
                      onClick={handleShowTerms}
                      className="text-blue-600 hover:text-blue-800 font-medium underline transition-colors duration-200"
                    >
                      Terms and Conditions
                    </button>{" "}
                    and acknowledge the Privacy Policy
                  </label>
                </div>
              </div>

              {/* Submit Button */}
              <div className="pt-6">
                <button
                  type="submit"
                  disabled={isSubmitting || !isFormComplete}
                  className={`w-full py-4 px-6 rounded-lg font-semibold text-white transition-all duration-300 transform ${
                    isSubmitting
                      ? "bg-blue-400 cursor-not-allowed"
                      : isFormComplete
                      ? "bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700 hover:shadow-lg hover:scale-105"
                      : "bg-gray-400 cursor-not-allowed"
                  } focus:outline-none focus:ring-4 focus:ring-blue-300 focus:ring-opacity-50`}
                >
                  {isSubmitting ? (
                    <div className="flex items-center justify-center">
                      <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                      </svg>
                      Creating Account...
                    </div>
                  ) : (
                    "Create Account"
                  )}
                </button>
              </div>

              {/* Login Link */}
              <div className="text-center pt-4">
                <p className="text-gray-600 text-sm">
                  Already have an account?{" "}
                  <button
                    type="button"
                    onClick={() => navigate("/login")}
                    className="text-blue-600 hover:text-blue-800 font-medium transition-colors duration-200"
                  >
                    Sign in here
                  </button>
                </p>
              </div>
            </form>
          </div>
        </div>
      </div>

      {/* Terms and Conditions Modal */}
      {showTerms && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4 backdrop-blur-sm">
          <div className="bg-white rounded-2xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-hidden">
            <div className="bg-gradient-to-r from-blue-600 to-purple-600 p-4">
              <h3 className="text-xl font-bold text-white">Terms and Conditions</h3>
            </div>
            {termsContent}
            <div className="p-4 border-t bg-gray-50 flex justify-end">
              <button
                type="button"
                onClick={() => setShowTerms(false)}
                className="px-6 py-2 bg-gradient-to-r from-blue-600 to-purple-600 text-white rounded-lg hover:from-blue-700 hover:to-purple-700 focus:outline-none focus:ring-2 focus:ring-blue-500 transition-all duration-200"
              >
                I Understand
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Register;